/** The document's title, set from the page the shell is showing — **F-008**.
 *
 * `index.html` carried a static `<title>EmitIQ</title>` and nothing ever changed it, so every tab,
 * every history entry and every bookmark was the same string, and a screen reader announced the same
 * title on every navigation. Of the things a person has to tell their own tabs apart by, this is the
 * only one the browser supplies.
 *
 * **One place builds the string**, for the reason `kindLabel` is the only way out of `kinds.ts`: a
 * suffix typed into two components is two suffixes within a month. The page name comes from the
 * `title` the shell already takes, so the heading a sighted reader sees and the title a tab shows are
 * the same expression rather than two that can disagree.
 *
 * **The cleanup restores the bare name**, not the previous page's. A screen with no shell (sign-in,
 * the claim link) would otherwise inherit whatever signed-in screen was last on show, and a tab
 * titled "Evidence" over a sign-in form is a small lie about where the reader is.
 */

import { useEffect } from "react";

/** What the product is called in a tab. */
export const SITE_TITLE = "EmitIQ";

/** `Evidence — EmitIQ`; the bare name where there is no page. An em dash, not a hyphen: a hyphen is
 * indistinguishable from the hyphens inside a page name such as "Record · gas · Trafford Park". */
export function documentTitle(page?: string, tab?: string): string {
  const name = page?.trim();
  if (!name) return SITE_TITLE;
  // **The tab comes first** (review item 15, WCAG 2.4.2): a place has up to eight tabs and they are
  // eight different screens, so "Processing - EmitIQ" on all four of its tabs told the history, the
  // tab strip and a screen reader's list of pages nothing apart. A tab that only repeats the place adds
  // nothing, and a tab with no place is not a title.
  const part = tab?.trim();
  return part && part !== name ? `${part} — ${name} — ${SITE_TITLE}` : `${name} — ${SITE_TITLE}`;
}

export function useDocumentTitle(page?: string, tab?: string): void {
  useEffect(() => {
    document.title = documentTitle(page, tab);
    return () => {
      document.title = SITE_TITLE;
    };
  }, [page, tab]);
}
